import React, {
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Drawer, Modal, Spin } from 'antd';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { DayJs } from '../../common/Utils';
import {
  CashActivity,
  useBusinessContext,
} from '../../context/BusinessContext';
import { useConnection, useUser } from '../../connection/connection';
import { useEvents } from '../../common/EventContext';
import { AlertFilled, DeleteOutlined } from '@ant-design/icons';
import { OptionedInputValue } from '../../common/OptionedInput';
import { useCustomRates } from '../../common/CustomRates';

export type IncomeModalInstance = {
  open: (siteId?: string, accountId?: string) => void;
  openTx: (txId: number) => void;
};

type Props = {};

const IncomesModal: ForwardRefRenderFunction<IncomeModalInstance, Props> = (
  props,
  ref,
) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const business = useBusinessContext();
  const connection = useConnection();
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const events = useEvents();
  const [title, setTitle] = useState<string>('Create new income');

  const [category, setCategory] = useState<string>();
  const [incomeId, setIncomeId] = useState<number>();
  const [selectedCurrencyAmount, setSelectedCurrencyAmount] = useState<
    OptionedInputValue | undefined
  >(undefined);
  const [otherAccountCurrency, setOtherAccountCurrency] = useState<
    string | undefined
  >(undefined);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { clearCustomRates, getCustomRate, setCustomRate, hasRateChanged } =
    useCustomRates();

  useEffect(() => {
    if (
      selectedCurrencyAmount?.option &&
      otherAccountCurrency &&
      selectedCurrencyAmount.option !== otherAccountCurrency
    ) {
      business
        .getCurrencyRate(
          selectedCurrencyAmount.option,
          otherAccountCurrency,
          selectedDate,
        )
        .then((rate) => {
          setCustomRate(
            selectedDate,
            selectedCurrencyAmount.option,
            otherAccountCurrency,
            rate.rate,
            false,
            false,
          );
        });
    }
  }, [
    selectedCurrencyAmount,
    otherAccountCurrency,
    // business.currencyRates,
    business,
    selectedDate,
    setCustomRate,
  ]);

  const user = useUser();
  useImperativeHandle(
    ref,
    () => ({
      open: (type, siteId?: string, accountId?: string) => {
        setVisible(true);
        form.resetFields();
        setTitle('Create new income');
        setSelectedCurrencyAmount(undefined);
        setOtherAccountCurrency(undefined);
        setSelectedDate(new Date());
        clearCustomRates();
      },
      openTx: (txId: number) => {
        setVisible(true);
        setLoading(true);
        setIncomeId(txId);
        setTitle('Edit transaction');
        form.resetFields();
        connection
          .get(`incomes/${business.businessId}/${txId}`)
          .then((res) => {
            form.setFieldsValue(res.data);
          })
          .finally(() => setLoading(false));
      },
    }),
    [form, clearCustomRates, connection, business.businessId],
  );

  const schema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'date',
        type: 'date',
        name: 'Date',
        layout: { xs: 12 },
        props: {
          disabledDate: (current: any) => {
            return current && current > DayJs().endOf('day');
          },
        },
        options: {
          rules: [{ required: true, message: 'Date is required' }],
        },
      },
      {
        id: 'siteId',
        type: 'select2',
        name: 'Site',
        layout: { xs: 12 },
        props: {
          showSearch: true,
          optionFilterProp: 'label',
          options: business.select.sites,
        },
        options: {
          rules: [{ required: true, message: 'Site is required' }],
        },
      },
      {
        id: 'category',
        type: 'select2',
        name: 'Category',
        layout: { xs: 12 },
        props: {
          showSearch: true,
          optionFilterProp: 'label',
          options: business.select['incomeTypes'],
        },
        options: {
          rules: [{ required: true, message: 'Category is required' }],
        },
      },
      {
        id: 'account',
        type: 'select2',
        name: 'Account',
        layout: { xs: 12 },
        props: (values: any) => {
          return {
            showSearch: true,
            optionFilterProp: 'label',
            options: Object.values(business.accounts)
              .filter((acc) => acc.activity)
              // .filter((acc) => !acc.siteId || acc.siteId === values.siteId)
              .map((acc) => ({ label: acc.name, value: acc.accountId })),
          };
        },
        options: {
          rules: [{ required: true, message: 'Account is required' }],
        },
      },
      {
        id: 'refNo',
        type: 'string',
        name: 'Ref Number',
        layout: { xs: 12 },
        props: {
          placeholder: 'Reference',
        },
        options: {
          rules: [{ required: true, message: 'Ref number is required' }],
        },
      },
      {
        id: 'vatType',
        type: 'select2',
        name: 'Vat',
        layout: { xs: 12 },
        props: {
          options: [
            { label: 'Apply VAT', value: 'Apply VAT' },
            { label: 'Apply SVAT', value: 'Apply SVAT' },
            { label: 'No VAT', value: 'No VAT' },
          ],
        },
        options: {
          rules: [{ required: true, message: 'Vat is required' }],
        },
      },
      {
        id: 'vatAmount',
        type: 'number',
        name: 'Vat Amount',
        layout: { xs: 12 },
        visible: (v) => v.vatType && v.vatType !== 'No VAT',
        props: {
          placeholder: 'Vat amount',
          min: 0,
        },
        options: {
          rules: [{ required: true, message: 'Vat amount is required' }],
        },
      },
      {
        id: 'amount',
        type: 'price',
        name: 'Amount',
        layout: { xs: 12 },
        props: {
          placeholder: 'Amount',
          min: 0,
        },
        options: {
          rules: [{ required: true, message: 'Amount is required' }],
        },
      },
      // {
      //   id: 'status',
      //   type: 'select2',
      //   name: 'Status',
      //   layout: { xs: 12 },
      //   props: {
      //     options: business.select['incomeStatus'],
      //   },
      //   options: {
      //     rules: [{ required: true, message: 'Status is required' }],
      //   },
      // },
      {
        id: 'ipaNo',
        type: 'string',
        name: 'IPA Number',
        layout: { xs: 12 },
        props: {
          placeholder: 'IPA number',
        },
        options: {
          rules: [{ required: false, message: 'IPA number is required' }],
        },
      },
      {
        id: 'ipaAmount',
        type: 'price',
        name: 'IPA Amount',
        layout: { xs: 12 },
        props: {
          placeholder: 'IPA amount',
          min: 0,
        },
        options: {
          rules: [{ required: false, message: 'IPA amount is required' }],
        },
      },
      {
        id: 'ipcNo',
        type: 'string',
        name: 'IPC Number',
        layout: { xs: 12 },
        props: {
          placeholder: 'IPC number',
        },
        options: {
          rules: [{ required: false, message: 'IPC number is required' }],
        },
      },
      {
        id: 'ipcAmount',
        type: 'price',
        name: 'IPC Amount',
        layout: { xs: 12 },
        props: {
          placeholder: 'IPC amount',
          min: 0,
        },
        options: {
          rules: [{ required: false, message: 'IPC amount is required' }],
        },
      },
      {
        id: 'description',
        type: 'text',
        name: 'Description',
        layout: { xs: 24 },
        props: { placeholder: 'Write a Description' },
      },
    ];
    return formSchema;
  }, [business]);

  const submit = useCallback(
    (values: any) => {
      setSaving(true);
      (incomeId
        ? connection.patch(`incomes/${business.businessId}/${incomeId}`, values)
        : connection.post(`incomes/${business.businessId}`, values)
      )
        .then((res: any) => {
          setVisible(false);
          events.emit('INCOMES', {
            action: incomeId ? 'UPDATE' : 'CREATE',
            data: res.data,
          });
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [business.businessId, connection, events, incomeId],
  );

  const deleteTx = useCallback(() => {
    // setSaving(true);
    // connection
    //   .delete(`transactions/${business.businessId}/activities/${txId}`)
    //   .then((res) => {
    //     setVisible(false);
    //     events.emit('TRANSACTIONS', {
    //       action: 'DELETE',
    //       data: txId,
    //     });
    //     events.emit('ACTIVITIES', {
    //       action: 'DELETE',
    //       data: txId,
    //     });
    //   })
    //   .finally(() => {
    //     setSaving(false);
    //   });
  }, []);

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={() => setVisible(false)}
      destroyOnClose={true}
      footer={
        <div className="flex justify-between gap-1">
          {/*{incomeId && (*/}
          {/*  <Button*/}
          {/*    onClick={() =>*/}
          {/*      Modal.confirm({*/}
          {/*        title: 'Delete Income',*/}
          {/*        content: 'Are you sure you want to delete this income?',*/}
          {/*        okButtonProps: { danger: true },*/}
          {/*        onOk: deleteTx,*/}
          {/*      })*/}
          {/*    }*/}
          {/*    danger={true}*/}
          {/*    icon={<DeleteOutlined />}*/}
          {/*  >*/}
          {/*    Delete*/}
          {/*  </Button>*/}
          {/*)}*/}
          <div className="w-full flex justify-end gap-1">
            <Button onClick={() => setVisible(false)}>Cancel</Button>
            <Button
              onClick={() => form.submit()}
              type="primary"
              loading={saving}
            >
              Save
            </Button>
          </div>
        </div>
      }
      width={600}
    >
      <Spin spinning={loading}>
        <Form layout="vertical" form={form} onFinish={submit}>
          <Form.Items schema={schema} />
        </Form>
      </Spin>
    </Drawer>
  );
};

export const useIncomesModal = () => useRef<IncomeModalInstance>(null);

export default React.forwardRef<IncomeModalInstance, Props>(IncomesModal);
